// Marketing › Ads and calls, Blueprint only (pages/ux-full screens/ads.js: #/home/ads, #/home/ads/campaigns,
// #/home/ads/leads), on DashboardPage (BL-189). Each page answers one question, each section drawn from its
// own action's answer: how the ads did (reports.campaigns by channel, adSpend.list), which campaigns work
// (reports.campaigns by campaign beside campaigns.list, and reports.callReasons), and where today's calls
// came from (calls.recent, and adConversions.list for what went to Google, each a page at a time). How
// the ads did also holds the ads over time (reports.campaigns by day or week), a notice when an ad
// account's numbers stop coming in (adAccounts.status), and the suggested changes to make in the ad
// platforms by hand (AdChanges.tsx). What each table and row says is ui-web's rows/ads-rows.ts; who sees
// them is ads-model.ts's (marketing.view).
import { useCallback, useEffect, useState } from 'react';
import {
  AdsTrendProps,
  CallReasonsProps,
  CallsBySourceProps,
  CampaignsReport,
  CampaignTableProps,
  ConversionsProps,
  money,
  plural,
  SpendProps,
  type OpenTarget,
} from '@flowology/ui-web';
import {
  DataTable,
  Figure,
  Figures,
  RowList,
  Segmented,
  SelectField,
  TextField,
  adsFigures,
  callColumns,
  callerOf,
  campaignColumns,
  campaignLines,
  campaignsLine,
  channelColumns,
  channelLines,
  conversionRows,
  conversionsLine,
  funnelRows,
  reasonColumns,
  reasonLines,
  reasonsLine,
  spendMonth,
  spendRows,
  trendColumns,
  trendHasAny,
  trendLines,
} from '@flowology/ui-web/fl';
import { DashboardPage, type DashboardSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { monthName, monthOf, todayIn } from '../books/model.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { customerHref } from '../history/customer-link.ts';
import { stepMonth } from '../huddle/huddle-model.ts';
import {
  allSpend,
  maySeeConversions,
  monthRange,
  periodRange,
  PERIODS,
  leadsInput,
  sourceCountsInput,
  sources,
  trendRange,
  TREND_PRESETS,
  type Period,
  type SourceCount,
  type SpendPage,
  type TrendPreset,
} from './ads-model.ts';
import { useAdChanges } from './AdChanges.tsx';
import { answered, firstOf } from './dash-sections.ts';
import './reports-page.css';

const tzOf = (nav: NavProps) => nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

/** A list a page at a time: the input's first page, then each next one under it, with the true total. */
function usePages<T>(action: string, input: Record<string, unknown> | null, field: string) {
  const key = JSON.stringify(input);
  const [page, setPage] = useState<{ key: string; rows: T[]; total: number; next: string | null } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(
    async (cursor?: string) => {
      if (input === null) return;
      try {
        const r = await client.call<Record<string, unknown>>(action, cursor ? { ...input, cursor } : input);
        const rows = (r[field] ?? []) as T[];
        setPage((p) => ({
          key,
          rows: cursor && p?.key === key ? [...p.rows, ...rows] : rows,
          total: typeof r.total === 'number' ? r.total : rows.length,
          next: typeof r.next === 'string' ? r.next : null,
        }));
        setError(null);
      } catch (e) {
        setError(errorText(e, 'Could not load this.'));
      }
    },
    // the input is compared by value (key), as useQuery's
    [action, key, field],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const data = page?.key === key ? page : null;
  return {
    data,
    error,
    reload: () => load(),
    more: data?.next ? () => void load(data.next!) : undefined,
  };
}

/** Every ad spend entry in a range (list caps M11), as useQuery answers: the month's sum is of them all. */
function useAllSpend(range: { from: string; to: string }) {
  const key = JSON.stringify(range);
  const [got, setGot] = useState<{ key: string; entries: unknown[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      const entries = await allSpend(
        (input) => client.call<SpendPage<unknown>>('adSpend.list', input),
        range,
      );
      setGot({ key, entries });
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Could not load this.'));
    }
    // the range is compared by value (key), as useQuery's
  }, [key]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data: got?.key === key ? { entries: got.entries } : null, error, reload };
}

/** What stops an ad account's numbers coming in (adAccounts.status), said once at the top; null with none. */
function useAccountNotices() {
  const q = useQuery<{ notices: { key: string; text: string }[] }>('adAccounts.status', {});
  if (!q.data?.notices.length) return null;
  return (
    <>
      {q.data.notices.map((n) => (
        <p key={n.key} className="bp-rep-bad" role="alert">
          {n.text} Setup › Campaigns shows each account.
        </p>
      ))}
    </>
  );
}

/** How the ads did, day by day or week by week, for a preset or picked days. */
function useOverTime(today: string): DashboardSection {
  const [preset, setPreset] = useState<TrendPreset>('30');
  const [picked, setPicked] = useState({ from: '', to: today });
  const [all, setAll] = useState(false);
  const range = trendRange(preset, today, picked);
  const q = useQuery<unknown>('reports.campaigns', 'error' in range ? null : range);
  const controls = (
    <div className="bp-rep-range">
      <Segmented
        label="Days"
        items={TREND_PRESETS.map(([id, label]) => ({ id, label }))}
        selectedKey={preset}
        onSelectionChange={(k) => {
          setPreset(k as TrendPreset);
          setAll(false);
        }}
      />
      {preset === 'custom' && (
        <>
          <TextField
            label="From"
            type="date"
            width="date"
            value={picked.from}
            max={today}
            onChange={(from) => setPicked({ ...picked, from })}
          />
          <TextField
            label="To"
            type="date"
            width="date"
            value={picked.to}
            onChange={(to) => setPicked({ ...picked, to })}
          />
        </>
      )}
    </div>
  );
  const base = { title: 'Over time', what: 'the days' };
  if ('error' in range)
    return {
      ...base,
      children: (
        <>
          {controls}
          <p className="bp-rep-sub">{range.error}</p>
        </>
      ),
    };
  const label = `${range.groupBy === 'week' ? 'By week' : 'By day'}, ${range.from} to ${range.to}`;
  const s = answered(
    q,
    base,
    (d: object) => AdsTrendProps.safeParse({ ...d, unit: range.groupBy, label }),
    (p) => {
      if (!trendHasAny(p))
        return { children: <p className="bp-rep-sub">No calls, leads, jobs or spend in these days.</p> };
      const lines = trendLines(p);
      const shown = firstOf(lines.rows, 7, all, () => setAll(true));
      return {
        children: (
          <>
            <p className="bp-rep-sub">{p.label}, newest first</p>
            <DataTable
              columns={trendColumns(p.unit)}
              rows={shown.rows}
              foot={lines.total}
              more={shown.more}
              empty="No calls, leads, jobs or spend in these days."
            />
            {p.adSpendNote && <p className="bp-rep-sub">{p.adSpendNote}</p>}
          </>
        ),
      };
    },
  );
  // the presets stay above the table whatever its state: a section that isn't ready keeps its own words
  return s.status && s.status !== 'ready'
    ? s
    : {
        ...s,
        children: (
          <>
            {controls}
            {s.children}
          </>
        ),
      };
}

// ── How did the ads do? ──

export function AdsScreen({ nav }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const today = todayIn(tz);
  const [period, setPeriod] = useState<Period>('today');
  const [allChannels, setAllChannels] = useState(false);
  const month = monthRange(today);
  const q = useQuery<unknown>('reports.campaigns', { ...periodRange(period, today), groupBy: 'channel' });
  const m = useQuery<unknown>('reports.campaigns', { ...periodRange('month', today), groupBy: 'channel' });
  const spend = useAllSpend(month);
  const crumb = usePageCrumb(nav);
  const overTime = useOverTime(today);
  const notices = useAccountNotices();
  const changes = useAdChanges({ timeZone: tz, held: nav.permissions ?? [] });
  const words = PERIODS.find(([p]) => p === period)![1];
  const totals = q.data ? CampaignsReport.safeParse(q.data) : null;
  const report = totals?.success ? totals.data : null;
  const note = [
    period === 'today' ? 'Completed, invoiced and revenue fill in as today’s jobs finish.' : null,
    report?.adSpendNote ?? null,
  ].filter(Boolean);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'How the ads did',
            lede: 'What the ads cost, the calls they brought, and what was booked, completed and invoiced from them.',
            actions: (
              <Segmented
                label="Period"
                items={PERIODS.map(([id, label]) => ({ id, label }))}
                selectedKey={period}
                onSelectionChange={(k) => {
                  setPeriod(k as Period);
                  setAllChannels(false);
                }}
              />
            ),
            ...(report ? { figures: <Figures items={adsFigures(report)} /> } : {}),
          }}
          {...(notices ? { notice: notices } : {})}
          bar={
            <p className="bp-rep-bar">
              <b>{period === 'today' ? 'Today, so far' : `${words}, to today`}</b>
              {note.length > 0 && <span className="bp-rep-sub">{note.join(' ')}</span>}
            </p>
          }
          lead={[
            answered(
              q,
              { title: 'By channel', what: 'the ads' },
              (d) => CampaignsReport.safeParse(d),
              (p) => {
                if (!p.rows.length) return { status: 'empty', emptyText: 'No calls, leads or jobs yet.' };
                const shown = firstOf(channelLines(p), 6, allChannels, () => setAllChannels(true));
                return {
                  children: (
                    <>
                      <DataTable
                        columns={channelColumns}
                        rows={shown.rows}
                        more={shown.more}
                        empty="No calls, leads or jobs yet."
                      />
                      <p className="bp-rep-sub">
                        Each job counts for the campaign it was booked under. Revenue is invoiced, before tax,
                        less credit notes.
                      </p>
                    </>
                  ),
                };
              },
            ),
          ]}
          main={[
            answered(
              spend,
              { title: 'Spend this month', what: 'the month’s spend' },
              (d: object) => SpendProps.safeParse({ ...d, ...month, today }),
              (p) => {
                if (!p.entries.length)
                  return { status: 'empty', emptyText: 'No ad spend recorded this month.' };
                const s = spendMonth(p);
                return {
                  children: (
                    <>
                      <Figure
                        label="Spent on ads this month"
                        value={money(s.totalCents)}
                        sub={`day ${s.gone} of ${s.days}`}
                      />
                      <RowList rows={spendRows(s)} label="Spend this month by platform" />
                      <p className="bp-rep-sub">
                        At this rate the month ends near <b>{money(s.projectedCents)}</b>.
                      </p>
                      {s.unlinked.length > 0 && (
                        <p className="bp-rep-sub">
                          {money(s.unlinkedCents)} is on {plural(s.unlinked.length, 'ad campaign')} not linked
                          to a campaign yet: {s.unlinked.join(', ')}.
                        </p>
                      )}
                    </>
                  ),
                };
              },
            ),
          ]}
          side={[
            answered(
              m,
              { title: 'From call to invoice', what: 'the month' },
              (d) => CampaignsReport.safeParse(d),
              (p) => {
                const rows = funnelRows(p.rows);
                if (!rows) return { status: 'empty', emptyText: 'No calls from the ads yet.' };
                return {
                  children: (
                    <>
                      <p className="bp-rep-sub">All ads this month</p>
                      <RowList rows={rows} label="From call to invoice" />
                    </>
                  ),
                };
              },
            ),
          ]}
          below={[overTime, changes.section]}
          layers={changes.sheet}
        />
      </main>
    </div>
  );
}

// ── Which campaigns work? ──

export function AdCampaignsScreen({ nav }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const thisMonth = monthOf(todayIn(tz));
  const [[from, to], setMonth] = useState(thisMonth);
  const [allCampaigns, setAllCampaigns] = useState(false);
  const [allReasons, setAllReasons] = useState(false);
  const q = useQuery<unknown>('reports.campaigns', { from, to, groupBy: 'campaign' });
  const c = useQuery<{ campaigns: unknown[] }>('campaigns.list', { includeRetired: true });
  const reasons = useQuery<unknown>('reports.callReasons', { from, to });
  const crumb = usePageCrumb(nav);
  const step = (by: 1 | -1) => {
    setMonth(stepMonth(from, by));
    setAllCampaigns(false);
    setAllReasons(false);
  };
  const month = monthName(from);
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Campaigns',
            lede: 'Which campaigns bring work back for what they cost, best first, and why people called.',
            actions: (
              <Segmented
                label="Month"
                selectionMode="none"
                items={[
                  { id: 'back', label: `‹ ${monthName(stepMonth(from, -1)[0]).split(' ')[0]}` },
                  { id: 'this', label: 'This month', isDisabled: from === thisMonth[0] },
                  { id: 'next', label: `${monthName(stepMonth(from, 1)[0]).split(' ')[0]} ›` },
                ]}
                onAction={(id) => {
                  if (id === 'this') {
                    setMonth(thisMonth);
                    setAllCampaigns(false);
                    setAllReasons(false);
                  } else step(id === 'back' ? -1 : 1);
                }}
              />
            ),
          }}
          main={[
            answered(
              { ...q, data: q.data && c.data ? q.data : null, error: q.error ?? c.error },
              { title: `Campaigns, ${month}`, id: 'campaigns', what: 'the campaigns' },
              (d: object) =>
                CampaignTableProps.safeParse({
                  ...d,
                  label: 'Best return first',
                  campaigns: c.data?.campaigns ?? [],
                }),
              (p) => {
                if (!p.rows.length)
                  return { status: 'empty', emptyText: 'No calls, leads or jobs on a campaign yet.' };
                const together = campaignsLine(p);
                const shown = firstOf(campaignLines(p), 5, allCampaigns, () => setAllCampaigns(true));
                const notes = [p.adSpendNote, p.grossProfitNote].filter(Boolean);
                return {
                  children: (
                    <>
                      <p className="bp-rep-sub">
                        {p.label}
                        {together ? `. ${together}` : ''}
                      </p>
                      <DataTable
                        columns={campaignColumns(p)}
                        rows={shown.rows}
                        more={shown.more}
                        empty="No calls, leads or jobs on a campaign yet."
                      />
                      {notes.length > 0 && <p className="bp-rep-sub">{notes.join(' ')}</p>}
                    </>
                  ),
                };
              },
            ),
            answered(
              reasons,
              { title: 'Why they called', what: 'the calls' },
              (d: object) => CallReasonsProps.safeParse({ ...d, label: `Calls taken in ${month}` }),
              (p) => {
                if (!p.rows.length) return { status: 'empty', emptyText: 'No calls taken yet.' };
                const lines = reasonLines(p);
                const shown = firstOf(lines.rows, 5, allReasons, () => setAllReasons(true));
                return {
                  children: (
                    <>
                      <p className="bp-rep-sub">{reasonsLine(p)}</p>
                      <DataTable
                        columns={reasonColumns}
                        rows={shown.rows}
                        foot={lines.total}
                        more={shown.more}
                        empty="No calls taken yet."
                      />
                    </>
                  ),
                };
              },
            ),
          ]}
        />
      </main>
    </div>
  );
}

// ── Where did today's calls come from? ──

export function AdLeadsScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const today = todayIn(tz);
  const [source, setSource] = useState('all');
  const [allCalls, setAllCalls] = useState(false);
  // today's inbound calls, in the company's day, from the source picked: filtered, counted and paged by
  // the server; each source's count is the server's too, of every call, not of the pages read (BL-110)
  const q = usePages<unknown>('calls.recent', leadsInput(today, source), 'calls');
  const counts = useQuery<{ sources?: SourceCount[] }>('calls.recent', sourceCountsInput(today));
  const conversions = maySeeConversions(nav.permissions ?? []);
  const conv = usePages<unknown>('adConversions.list', conversions ? {} : null, 'conversions');
  const crumb = usePageCrumb(nav);
  const calls = q.data?.rows ?? [];
  const all = sources(counts.data?.sources ?? []);
  const total = q.data?.total ?? 0;
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Calls by source',
            lede: 'Where each of today’s calls came from, the number it rang, why they called, and what came of it.',
            ...(all[0]!.n > 0
              ? {
                  // a select, not a segmented control: there can be any number of sources, and on a
                  // phone they'd run off the page
                  actions: (
                    <SelectField
                      label="Source"
                      options={all.map((s) => ({ value: s.key, label: `${s.label} (${s.n})` }))}
                      value={source}
                      onChange={(k) => {
                        setSource(k);
                        setAllCalls(false);
                      }}
                    />
                  ),
                }
              : {}),
          }}
          main={[
            answered(
              q,
              { title: 'Today', what: 'today’s calls' },
              () => CallsBySourceProps.safeParse({ label: 'Newest first', timeZone: tz, calls }),
              (p) => {
                if (!p.calls.length) return { status: 'empty', emptyText: 'No calls yet today.', count: 0 };
                const shown = firstOf(p.calls, 5, allCalls, () => setAllCalls(true));
                const next = q.more
                  ? {
                      label: `The next ${Math.min(100, total - calls.length)} of ${total} calls`,
                      onPress: q.more,
                    }
                  : null;
                return {
                  count: total,
                  children: (
                    <>
                      <p className="bp-rep-sub">
                        {p.label}, {plural(p.calls.length, 'call')}
                      </p>
                      <DataTable
                        columns={callColumns(tz)}
                        rows={shown.rows}
                        hrefOf={(c) => {
                          const id = callerOf(c).customerId;
                          return id ? customerHref(id) : null;
                        }}
                        onOpen={(c) => {
                          const id = callerOf(c).customerId;
                          if (id) onOpen({ kind: 'customer', id });
                        }}
                        more={shown.more ?? next}
                        empty="No calls yet today."
                      />
                    </>
                  ),
                };
              },
            ),
            ...(conversions
              ? [
                  answered(
                    conv,
                    { title: 'Sent to Google', what: 'what was sent to Google' },
                    (r: { rows: unknown[]; total: number; next: string | null }) =>
                      ConversionsProps.safeParse({
                        conversions: r.rows,
                        total: r.total,
                        next: r.next,
                        timeZone: tz,
                      }),
                    (p) =>
                      p.conversions.length
                        ? {
                            children: (
                              <>
                                <p className="bp-rep-sub">{conversionsLine(p)}</p>
                                <RowList
                                  rows={conversionRows(p, (id) => onOpen({ kind: 'job', id }))}
                                  first={5}
                                  label="Sent to Google"
                                  {...(conv.more ? { onMore: conv.more } : {})}
                                />
                              </>
                            ),
                          }
                        : { status: 'empty', emptyText: 'Nothing has been sent to Google yet.' },
                  ),
                ]
              : []),
          ]}
        />
      </main>
    </div>
  );
}
